// Post-processing pipeline (WS-4), one quality (WS-72 step 5), on WebGPURenderer + TSL (WS-75 core).
//
//   pass(scene, camera): MSAA 4, HalfFloat, with a depth texture
//   -> finish (rendered once into its own HalfFloat target; the phone and rescue sets: inside the output pass): NaN/Inf guard (WS-15) + depth-only ambient occlusion
//      (half res, HalfAONode: the AO and its 4x4 blur) + selection depth of field + season grade + warmth + vignette
//   -> + bloom (BloomPassesNode: three's BloomNode, the UnrealBloomPass port, in 10 passes instead of 12, WS-77; the WS-15
//      firefly cap in its bright pass)
//   -> RenderPipeline output transform (tone mapping + sRGB)
//
// WS-75: TSL nodes on a RenderPipeline. Screen uv in TSL has y = 0 at the top (both backends); the AO normal and the
// golden-angle discs flip y, so screen up is -uv.y throughout.
//
// WS-72 step 5 (owner 2026-10-16/17: "one quality only, High or better; a weaker device gets a lighter method, never a
// lighter look"): every device draws today's High. Old inputs (?quality=, the stored choice, a preset's render.quality,
// setTier / pin / clearForced / onTier callers) are still accepted and change nothing. The 3D chain draws at up to 1.5 device px per CSS px (WS-78, HIGH.dpr below).
// WS-77: the old Balanced / Battery measuring table (?tiers=) and its FXAA pass are gone; ?tiers= is ignored like ?quality=.
// Phone set: the same chain with lighter methods (PHONE below): canvas = picture density, AO at half the samples and kept
// while the camera holds, bloom in 3 passes, a density rule for 30 pictures a second, the canvas following that density
// and the finish drawn inside the output pass (VIS-267 W7).
import * as THREE from 'three/webgpu';
import {
  Fn, pass, uniform, uv, vec2, vec3, vec4, float, uint, uvec4, mix, smoothstep, step, clamp, max, min, abs, cross, normalize, dot,
  length, cos, sin, floor, mod, screenCoordinate, select, luminance, pow, texture, passTexture, getViewPosition,
  context, convertToTexture, floatBitsToUint, any, If, Loop, array, int,
} from 'three/tsl';
import { qa } from './config.js'; // WS-49: QA parameters (draft hosts only under a preset)
import { sharedInstancePrograms, sharedValueBuilds, parallelPipelines, pending, compileAs, trackDepths, batchPipelines, warmBatchFor, compiles, onCompiled, dryDraw } from './gpu/pipelines.js'; // WS-75 perf; VIS-267: batches on the WebGL2 backend only
import { skipDarkLights } from './gpu/lighting.js'; // iGPU lane: a light at intensity 0 is skipped (a uniform branch)
import { sharedSamplers } from './gpu/samplers.js'; // VIS-272: textures of one sampling setting read through one sampler
import { watchPrograms } from './gpu/programs.js'; // a program that failed to build: tried again, reported, a plainer stand-in
import { degraded } from './degraded.js'; // VIS-27: a quiet failure is reported
import { canvasTarget, pipelines, nodes, restoreViewport, deviceLost } from './gpu/internals.js'; // VIS-194: three's private parts, checked at start-up
import { capture } from './capture.js'; // a capture's best quality: fullDensity is its switch

// dpr cap, MSAA samples, AO, bloom, DOF, shadow map size, tree detail distance (m), shadow softness
// WS-78 lane C: the 3D picture is drawn at up to 1.5 device pixels per CSS pixel (was 2). On a 2x laptop or a 3x phone
// that is 56 % of the pixels for every pass (the MSAA 4 scene, AO, finish, bloom, output, the canvas itself); the browser
// scales the canvas to the screen and the interface (labels, pins, buttons: HTML over the canvas) stays at full
// sharpness. Screens at 1.5x or less draw as before. A fixed rule by density, not a device class (one quality).
// The canvas and the last (output) pass stay at the screen's density up to CANVAS_DPR and upscale the 1.5 picture with a
// contrast-adaptive sharpen (createPipeline upscaled()).
// px: the most device pixels the 3D picture starts at (setInner): the start density is sqrt(px / the canvas's CSS
// area), never above `dpr` or the screen's own, never under the adaptive floor; the adaptive density may then climb to
// `dpr` on a fast GPU or step down. One rule by pixel count, no machine named (the owner's pick 2026-11-03 on same-camera
// pictures: "iMac 24 inch: B, 5K Studio Display: C, iPad (A16): B"): 4 million here, so an iMac 24 (2240 x 1140 CSS px
// at 2x) starts at 1.25 (his B; 1.5 drew 5.75 Mpx), a 5K (2560 x 1320) at 1.09 (7.6 Mpx at 1.5; his C was 0.9), a
// 13-16" laptop window at 1.5 as before. Before: the picture's pixels grew with the screen, and weaker Macs on big
// screens lagged.
export const CANVAS_DPR = 2;
export const HIGH = { label: 'High', dpr: 1.5, samples: 4, ao: true, bloom: true, dof: true, shadow: 4096, treeNear: 700, shadowRadius: 3, px: 4e6 };
// The adaptive density's rule (createPipeline adapt()); a set may override any field with its own `adapt`.
//   slow / fast: the median frame (ms) that steps down / up; n frames in the median; wait: ms after a change before the
//   next judgement; upAfter: ms since the last change before a step up; floor: the lowest density; lock: a step down that
//   did not help, or a level slow right after a step up, is never tried again this visit (else again after `retry` ms);
//   gpu: judge the drawn frame's own work (its start to the GPU finishing it: WebGPU onSubmittedWorkDone) instead of the
//   interval between frames (slowFrame / fastFrame then apply where the backend cannot tell, the WebGL2 fallback).
//   Computers too: Safari's intervals come in whole refreshes (16.7 / 33.3 / 50 ms), so a step down that cut the work
//   but stayed in one band read as "did not help", was undone and locked for the visit (the 38 / 55 lines, as work).
const ADAPT = { slow: 1000 / 38, fast: 1000 / 55, n: 45, wait: 1500, upAfter: 8000, floor: 0.75, lock: true, retry: Infinity, gpu: true };
// ---- VIS phone render set (begin): the 3D chain of the phone set (main.js PHONE_ON: a phone, ?phoneset=1; createPipeline
// `phone`). Same look as HIGH, lighter methods; computers and lobby screens draw HIGH. Mobile GPUs draw in tiles: every
// full-screen pass and every stored target is memory traffic, which is heat and battery. One object, cited by the rules
// (performance.md "One quality, one phone set").
//   dpr 1.25, canvas 1.5 (2026-10-31, the owner's pick C on the lighter phone set, PR 657: "density is fine"): the 3D chain
//     draws at 1.25 device px per CSS px (the scene's MSAA target, depth, AO, bloom: -13 MB of a phone's graphics memory,
//     31 % fewer pixels a pass) and the output pass writes it to a 1.5 canvas with the contrast-adaptive sharpen (an
//     upscale by 1.2, as a computer's 1.5 -> 2). Before (VIS-267 W7): canvas 1.5 = the picture's own density, the sharpen
//     at 1:1; before that a 2x canvas. The browser scales the canvas to the screen; labels, pins and buttons are HTML and
//     stay sharp.
//   px 1 million (the rule at HIGH.px; the owner's pick 2026-11-03, "iPad (A16): B"): an A16 iPad (1180 x 820 CSS px) starts at 1.02
//     (his B 1.0, 0.97 Mpx; 1.25 lagged), a bigger iPad at the floor 1.0 and climbs to 1.25 on a fast GPU; phones keep
//     1.25. The canvas stays 1.5 while the start holds (as in his pictures); it follows a level the adaptive density took.
//   follow (VIS-267 W7): the canvas also follows the adaptive density down (and back up), once a level has held (the
//     step's own verdict is in and FOLLOW_MS passed), so the output pass never upscales a lowered picture. The canvas
//     is resized only in render(), right before that frame draws into it (VIS-255: a resized canvas is blank until the
//     next draw; a resize outside a draw showed a blank map on some browsers).
//   oneOutput (VIS-267 W7): the finish (AO apply, depth of field, grade, split tone, warmth, vignette) runs inside the
//     output pass with the bloom add and the tone map + sRGB: no finish target, one full-screen pass less. The centre is
//     finished in full; the 1:1 sharpen's 4 neighbours and the bloom's bright reads go through finishLite (the AO from
//     its blurred half-size picture read bilinear, no DOF): the glow round a lit window is a little weaker in held frames
//     (up to 51 levels on ~0.5 % of a night close-up). Owner, 2026-10-23: "I personally see absolutely no difference so
//     whichever is easier on mobile". Rejected: the 4x depth unstored (a view-z MRT output): softer AO in foliage and
//     ~24 MB more graphics memory on iPhones (no transient attachments).
//   aoSamples 5: half the AO samples; HIGH's 4x4 pattern and its blur pass stay (with them the blur still averages 80
//     directions a pixel). Rejected: a 2x2 pattern blurred by the finish's own upsample (no blur pass): 20 directions drew
//     each tree, fence and road edge as blocky stair-step copies (VIS-249 crops). aoReuse 30: the AO picture is kept while
//     the camera holds (only the trees sway) and nothing on screen moves (`busy`: the shadow keeper's movers in view; a
//     kept AO trailed every reindeer and sled as a dark ghost, 2026-10-25) and redrawn, with its blur, when the view or projection changes, the drawing
//     size or AO radius changes, or every 30 drawn frames (~1 s at the phone's 30 pictures a second). ?aoreuse=0 draws it
//     every frame (A/B).
//   bloomMips: 2 mips in 3 passes instead of 5 in 10: 1/4 of the picture (a bright pass, each 4x4 block of the finish texel
//     by texel, then kernel 10 in one 2D pass), then 1/16 (kernel 18, one 2D pass). `share` hands each of HIGH's
//     5 mip weights to the phone mip nearest in size, so the glow keeps its total strength (the widest halo is tighter).
//   adapt: the density judges the frame against the phone's 30 pictures a second (main.js passes the phone clock's cap,
//     render() capHz, and the frame's start; densityLines below): down a step (0.25) when the median frame, from its
//     start to the GPU done, takes over 28 ms or the median time between pictures is over 36.7 ms (refreshes missed),
//     back up after 8 s under 19 ms at the cap's pace, floor 1.0, one change at most every 3 s, and never a lock for the
//     visit (a step that did not help is tried again after 30 s). WebGL2 fallback (no GPU signal): the interval between
//     drawn frames, down under 24, up over 28.5 pictures a second.
//   passes per picture (scene, AO, AO blur, bloom, output; shadow passes not counted, they are kept maps): before 14
//     (HIGH: 1 + 2 + 1 finish + 9 + 1), then 8 / 6 with a finish pass of its own, now 7 while the camera moves
//     (1 + 2 + 3 + 1) and 5 while it holds (the AO and its blur are kept). Live count: resortMap.quality.passes.
//     clouds 1 (VIS-272): the clouds add-on draws one more pass every picture while they are on (lazy/clouds.js PASSES:
//     the cloud pass, at half the picture's width and height; the composite is drawn inside the scene pass): 8 and 6
//     then, within the budget.
export const PHONE = { ...HIGH, label: 'Phone', dpr: 1.25, canvas: 1.5, px: 1e6, sharpen: true, follow: true, oneOutput: true, aoSamples: 5, aoReuse: 30,
  bloomMips: [{ div: 4, kernel: 10, share: [1, 1, 0.5, 0, 0] }, { div: 16, kernel: 18, twoD: true, share: [0, 0, 0.5, 1, 1] }],
  adapt: { slow: 28, fast: 19, slowFrame: 1000 / 24, fastFrame: 1000 / 28.5, n: 60, wait: 3000, upAfter: 8000, floor: 1, lock: false, retry: 30000, gpu: true },
  passes: { before: 14, moving: 7, still: 5, clouds: 1 } };
// VIS-267 W7: how long a density level holds before the canvas follows it (PHONE.follow), after the step's own verdict
export const FOLLOW_MS = 3000;
// VIS-267: the adaptive density's lines under a capped loop (render()'s capHz: the phone clock's 30). A screen shows a
// picture only at a refresh: one that misses 33.3 ms shows at 41.7 (120 Hz) or 50 ms (60 Hz), 24 or 20 a second, the
// owner's iPhone 15 Pro Max at 20-23 while its frame's draw alone (render() to the GPU done) stayed under the old 30 ms
// line. So, with the GPU's signal (gpu): the frame's work from its start (before the world step) to the GPU finishing
// it over 85 % of a capped frame steps down (28 ms at 30: a margin for the browser to show it at the next refresh),
// under 57 % up (19 ms) when the work scaled to the next level's pixels stays under the down line (densityVerdict: from
// 1.0 the next is 56 % more pixels, from an A16 iPad's 1.02 51 %); and the pace the screen got, the
// median time between busy pictures, over 110 % of the cap's (36.7 ms, lazy/framestats.js ON_TIME_30: more than half
// the pictures missed their refresh) steps down too, whatever the work says. Without it (the WebGL2 fallback): the
// interval between drawn frames, under 80 % of the cap's rate down, over 95 % up.
export function densityLines(capHz, gpu) {
  return gpu ? { slow: 850 / capHz, fast: 570 / capHz, late: 1100 / capHz } : { slow: 1000 / (capHz * 0.8), fast: 1000 / (capHz * 0.95), late: 0 };
}
const STEP = 0.25; // the adaptive density's step
/** The adaptive density's verdict (createPipeline judge()): null, or [the new density, why]. st (kept between calls):
 *  { check, checkBy, from, noLower, holdDown, raised, changed, failed: Map(density -> ms) }; m: the medians
 *  { work, pace } (pace 0 = not judged); lines: densityLines() or the set's own { slow, fast, late }; cur: the density
 *  now; top: the highest it may take; AP: the set's rule (ADAPT + T.adapt). A step down that did not make what took it
 *  (the pace or the work) 10 % faster is undone to st.from, the level it left (and no step down follows: for the visit
 *  with lock, else for AP.retry ms); a level slow within 4 s of a step up is not tried again (lock) or for AP.retry ms.
 *  VIS-267 round 2 C (lazy/sharp.js, the phone set): st.low > 0 (0.75: only a phone the opening test measured too slow) =
 *  one step under the floor, on the frame's work alone (never the pace), once a visit (no075); undone for the visit
 *  when it was not 10 % faster ('undo-low': the phone remembers it), back up after st.lowUp ms when the work scaled to
 *  the floor (x st.lowRatio) is under the fast line. */
export function densityVerdict(st, m, lines, cur, top, now, AP) {
  if (st.check) { // the step down just taken: did it help?
    const v = st.checkBy === 'pace' ? m.pace : m.work, helped = v < st.check * 0.9;
    st.check = 0;
    if (helped) return null;
    if (cur < AP.floor - 1e-6) return [AP.floor, 'undo-low'];
    if (AP.lock) st.noLower = true; else st.holdDown = now + AP.retry;
    return [st.from, 'undo'];
  }
  if (st.low > 0 && cur < AP.floor - 1e-6) return now - st.changed > st.lowUp && m.work * st.lowRatio < lines.fast ? [AP.floor, 'up'] : null;
  if (st.low > 0 && !st.no075 && cur < AP.floor + 1e-6 && m.work > lines.slow) { st.no075 = true; st.checkBy = 'work'; st.check = m.work; return [st.low, 'low']; }
  const late = lines.late > 0 && m.pace > lines.late, down = Math.max(AP.floor, cur - STEP);
  // a step down: one step, never under the floor, and only a real one (over 0.05): a pixel-limit start (HIGH.px) lies
  // between the steps, so its last step down ends on the floor (5K: 1.09 -> 0.84 -> 0.75; 6K: 0.92 -> 0.75; an A16
  // iPad's 1.02 over the phone set's floor 1 stays). An undo goes back to the level the step left (st.from)
  if ((late || m.work > lines.slow) && cur - down > 0.05 && !st.noLower && now >= st.holdDown) {
    if (now - st.raised < 4000) st.failed.set(cur, now); // too slow right after a step up
    st.checkBy = late ? 'pace' : 'work'; st.check = late ? m.pace : m.work; st.from = cur;
    return [down, late ? 'late' : 'down'];
  }
  // a step up only when the work scaled to its pixels still fits the frame the loop has: a capped loop's (the phone
  // clock's 30) slow line; else the fast one (a vsync screen: Safari shows a frame over a refresh a whole refresh late).
  // The pixel limit's start (iMac 24 at 1.25, an A16 iPad at 1.02) climbs only on a GPU with that much room
  const up = Math.min(top, cur + STEP), failed = st.failed.has(up) && (AP.lock || now - st.failed.get(up) < AP.retry);
  if (!late && m.work < lines.fast && m.work * (up / cur) ** 2 < (lines.late > 0 ? lines.slow : lines.fast) && cur < top - 1e-6 && now - st.changed > AP.upAfter && !failed) { st.raised = now; return [cur + STEP, 'up']; }
  return null;
}
// ---- VIS phone render set (end)
// VIS-239: the rescue set of a device that lost its graphics on the map before (main.js RESCUE, web/js/rescue.js): the
// phone set's chain with the canvas and the 3D chain at 1 px per CSS px, no ambient occlusion, no bloom (both build-time:
// the chain is built without them), no 1:1 sharpen. MSAA stays 4: the trees and lit windows draw
// by alpha to coverage, which without it is a coarse dither (VIS-255 try, 10 MB); the adaptive density may still step
// under 1 (floor 0.75). Passes: scene, output (VIS-267 W7: the finish inside it, as the phone set; before: scene,
// finish, output). Without AO, bloom or the 1:1 sharpen the merged finish draws the same picture. No pixel limit (px):
// it starts at 1 on every screen.
export const RESCUE = { ...PHONE, label: 'Rescue', dpr: 1, canvas: 1, px: Infinity, ao: false, bloom: false, sharpen: false, bloomMips: null,
  adapt: { ...PHONE.adapt, floor: 0.75 }, passes: { before: 3, moving: 2, still: 2 } };
const NAMES = ['auto', 'high', 'medium', 'low']; // what old presets, addresses and stored choices may still send (accepted, no effect)

const _quad = new THREE.QuadMesh(), _size = new THREE.Vector2();
let _state;

// ---------------------------------------------------------------- half-res ambient occlusion
// WS-63 owner fix: the fold threshold `bias` (0.08 = today) and gain `aoK` (1) are uniforms: createPipeline aoBias.
// Two quads a frame into half-res targets: the AO (10 golden-angle samples, a 4x4 rotation pattern) and the 4x4
// depth-aware blur that removes that pattern exactly. The finish node reads the blurred one (getTextureNode()).
// Phone set (PHONE): `samples` 5 (the same pattern and blur); `reuse` n > 0 keeps the AO picture while the view holds
// (see moved()), redrawn at the latest every n frames. `drawn` = the quads drawn in the last frame (pass count, QA).
class HalfAONode extends THREE.TempNode {
  static get type() { return 'HalfAONode'; }
  constructor(depthNode, camera, { samples = 10, reuse = 0 } = {}) {
    super('vec4');
    this.depthNode = depthNode;
    this.camera = camera;
    Object.assign(this, { samples, reuse });
    this.updateBeforeType = THREE.NodeUpdateType.FRAME;
    this.aoRT = new THREE.RenderTarget(1, 1, { type: THREE.UnsignedByteType, format: THREE.RedFormat, depthBuffer: false }); // R8: only .r is ever read (the same bytes, a quarter of the memory and of the writes)
    this.aoRT.texture.name = 'ao';
    this.blurRT = new THREE.RenderTarget(1, 1, { type: THREE.UnsignedByteType, format: THREE.RedFormat, depthBuffer: false });
    this.blurRT.texture.name = 'ao.blur';
    this.radius = uniform(2.6); this.bias = uniform(0.08); this.aoK = uniform(1);
    this.texel = uniform(new THREE.Vector2()); // one half-res pixel in uv
    this.depthTexel = uniform(new THREE.Vector2()); // one full-res (depth texture) pixel in uv
    this.projScale = uniform(new THREE.Vector2(1, 1)); // proj[0][0], proj[1][1]
    this.projInv = uniform(camera.projectionMatrixInverse);
    this.aoMat = new THREE.NodeMaterial(); this.aoMat.name = 'AO';
    this.blurMat = new THREE.NodeMaterial(); this.blurMat.name = 'AO blur';
    this._out = passTexture(this, this.blurRT.texture);
    this.age = Infinity; this.drawn = 0; this.sig = new Float64Array(35); // reuse: frames since the last draw, the view it drew
    this.busy = null; // reuse: a function that answers true while something on screen moves (main.js: the shadow keeper's movers in view): drawn again this picture
  }
  getTextureNode() { return this._out; }
  get halfTexel() { return this.texel; }
  /** the next frame draws the AO again (a kept picture of another scene: the warm start's empty one) */
  invalidate() { this.age = Infinity; }
  setSize(w, h) { // true when the half-res size changed (a capture: the AO at full size, as fine as a 2x picture's half-res one was; Film's recording draws at the video's own size)
    const s = capture.on ? 0 : 1, hw = Math.max(1, w >> s), hh = Math.max(1, h >> s), changed = this.aoRT.width !== hw || this.aoRT.height !== hh;
    if (changed) { this.aoRT.setSize(hw, hh); this.blurRT.setSize(hw, hh); }
    this.texel.value.set(1 / hw, 1 / hh);
    this.depthTexel.value.set(1 / Math.max(1, w), 1 / Math.max(1, h));
    return changed;
  }
  // reuse: did the view change since the AO was drawn? The camera's view matrix (rotation 1e-5, translation 1 mm: a
  // damped camera that has come to rest still moves by rounding) and projection, the AO radius (it follows the zoom),
  // bias and gain. Stores the view when it changed.
  moved() {
    const v = this.camera.matrixWorldInverse.elements, p = this.camera.projectionMatrix.elements, s = this.sig;
    let m = false;
    for (let i = 0; i < 16; i++) {
      if (Math.abs(v[i] - s[i]) > (i >= 12 && i < 15 ? 1e-3 : 1e-5)) m = true;
      if (Math.abs(p[i] - s[16 + i]) > 1e-6) m = true;
    }
    const k = [this.radius.value, this.bias.value, this.aoK.value];
    for (let i = 0; i < 3; i++) if (k[i] !== s[32 + i]) m = true;
    if (m) { s.set(v); s.set(p, 16); s.set(k, 32); }
    return m;
  }
  updateBefore({ renderer }) {
    if (this.off) { this.drawn = 0; this.age = Infinity; return; } // a seat in a room (render): not drawn, drawn anew after
    const s = renderer.getDrawingBufferSize(_size);
    const resized = this.setSize(s.x, s.y);
    const e = this.camera.projectionMatrix.elements;
    this.projScale.value.set(e[0], e[5]);
    const moved = this.reuse > 0 && this.moved(); // (always evaluated: it stores the view)
    if (this.reuse > 0 && !resized && !moved && !this.busy?.() && ++this.age < this.reuse) { this.drawn = 0; return; } // phone: kept while the camera holds and nothing on screen moves (a kept AO trailed every reindeer and sled as a dark ghost, 2026-10-25)
    this.age = 0;
    _state = THREE.RendererUtils.resetRendererState(renderer, _state);
    renderer.setClearColor(0xffffff, 1); // the sky (discarded below) stays unoccluded
    _quad.material = this.aoMat; _quad.name = 'AO';
    renderer.setRenderTarget(this.aoRT); _quad.render(renderer);
    _quad.material = this.blurMat; _quad.name = 'AO blur';
    renderer.setRenderTarget(this.blurRT); _quad.render(renderer);
    this.drawn = 2;
    THREE.RendererUtils.restoreRendererState(renderer, _state);
  }
  setup(builder) {
    const ctx = context(builder.getSharedContext());
    const depth = this.depthNode, projInv = this.projInv, texel = this.texel, radius = this.radius;
    // A half-res pixel's centre falls on the corner of four full-res depth texels; which one a nearest read picks then
    // flips from row to row with float rounding (thin horizontal bands on far snow under WebGPU / D3D12). Depth is read
    // half a full-res texel up-left of it (a quarter of a half-res pixel): always the same texel, as the WebGL pass got.
    const at = (u) => u.sub(texel.mul(0.25));
    // A point is rebuilt at the centre of the depth texel it read, not at the uv that read it. Far away the AO radius
    // is under a pixel: several samples read the same texel, and each rebuilt along its own ray put that one depth on a
    // plane facing the camera; on grazing far slopes that plane stood above the ground and occluded it (far hills drawn
    // at half brightness, in flat bands, 2026-10-18). At the texel's centre such a sample is the centre point itself
    // (no occlusion) and a sample in another texel lies on the real surface.
    const dt = this.depthTexel;
    const snap = (u) => floor(at(u).div(dt)).add(0.5).mul(dt);
    const viewPos = (u) => { const q = snap(u); return getViewPosition(q, depth.sample(q).x, projInv); };
    this.aoMat.contextNode = ctx;
    this.aoMat.fragmentNode = Fn(() => {
      const vUv = uv();
      depth.sample(at(vUv)).x.greaterThanEqual(0.99999).discard();
      const p = viewPos(vUv).toConst();
      // Normal from the depth buffer, picking the flatter neighbour on each axis to avoid edge halos.
      // (screen up = -uv.y here, so pu / pd and the cross product match the WebGL pass)
      const tx = vec2(texel.x, 0), ty = vec2(0, texel.y);
      const pr = viewPos(vUv.add(tx)).toConst(), pl = viewPos(vUv.sub(tx)).toConst();
      const pu = viewPos(vUv.sub(ty)).toConst(), pd = viewPos(vUv.add(ty)).toConst();
      const dx = select(abs(pr.z.sub(p.z)).lessThan(abs(p.z.sub(pl.z))), pr.sub(p), p.sub(pl));
      const dy = select(abs(pu.z.sub(p.z)).lessThan(abs(p.z.sub(pd.z))), pu.sub(p), p.sub(pd));
      const cr = cross(dx, dy).toConst();
      const n = select(dot(cr, cr).greaterThan(1e-20), normalize(cr), vec3(0, 0, 1)).toConst(); // WS-15: normalize(0) is NaN
      // World radius projected to screen, clamped so distant terrain doesn't cost more than near detail.
      const r = min(radius.mul(this.projScale).mul(0.5).div(p.z.negate()), vec2(0.06)).toConst();
      // 4x4 rotation pattern, removed exactly by the 4x4 blur pass (no frame jitter: there's no TAA to absorb it).
      // (phone: N = 5 samples: the blur still averages 16 rotations, 80 directions a pixel)
      const N = this.samples;
      const c4 = mod(floor(screenCoordinate.xy), 4.0);
      const ign = c4.x.mul(4).add(c4.y).div(16).add(0.03).toConst();
      const occ = float(0).toVar();
      for (let i = 0; i < N; i++) {
        const a = ign.mul(6.2832).add(i * 2.39996), k = Math.sqrt((i + 0.5) / N);
        const s = viewPos(vUv.add(vec2(cos(a), sin(a).negate()).mul(r).mul(k)));
        const v = s.sub(p).toConst(), dist = length(v).toConst();
        const fall = float(1).sub(smoothstep(radius.mul(0.6), radius.mul(1.6), dist)), h = dot(n, v).toConst(); // h: the sample's height over the surface
        // What stands higher than 0.3 m over the surface occludes less, nothing from 0.75 m (the owner's pick D,
        // 2026-10-26): a horse's belly, a sled seat or a walker's coat cast no wide dark ring on the snow round them, only
        // a small shade at their feet; wall feet, eaves, logs and folds keep theirs (and the forest floor loses its blots)
        occ.addAssign(max(0, h.div(dist.add(1e-3)).sub(this.bias)).mul(this.aoK).mul(fall).mul(float(1).sub(smoothstep(0.3, 0.75, h))));
      }
      const ao = clamp(float(1).sub(occ.div(N).mul(3.2)), 0, 1);
      return vec4(vec3(ao.mul(ao)), 1);
    })();
    this.aoMat.needsUpdate = true;
    const aoTex = texture(this.aoRT.texture);
    this.blurMat.contextNode = ctx;
    this.blurMat.fragmentNode = Fn(() => {
      const vUv = uv();
      const z = viewPos(vUv).z.toConst();
      const sum = float(0).toVar(), wsum = float(0).toVar();
      for (let x = 0; x < 4; x++) for (let y = 0; y < 4; y++) {
        const u = vUv.add(vec2(x - 1.5, y - 1.5).mul(texel));
        const w = float(1).div(float(0.01).add(abs(viewPos(u).z.sub(z)).div(max(1, z.negate().mul(0.03)))));
        sum.addAssign(aoTex.sample(u).r.mul(w)); wsum.addAssign(w);
      }
      return vec4(vec3(sum.div(wsum)), 1);
    })();
    this.blurMat.needsUpdate = true;
    return this._out;
  }
  dispose() { super.dispose(); for (const o of [this.aoRT, this.blurRT, this.aoMat, this.blurMat]) o.dispose(); }
}

// WS-15 guard: a single NaN/Inf pixel from a faulty scene shader would otherwise be spread by bloom's blur chain over the
// whole frame (a black frame). Non-finite pixels become black, negatives (MSAA-extrapolated colours) become 0. WGSL has
// no isnan / isinf (and compilers may assume finite maths), so the exponent bits are tested.
const EXP = 0x7f800000;
const nonFinite = (c) => any(floatBitsToUint(c).bitAnd(uvec4(uint(EXP))).equal(uvec4(uint(EXP))));
const safe = (c) => min(max(select(nonFinite(c), vec4(0, 0, 0, 1), c), vec4(0)), vec4(60000));

// WS-15 (round 4e): firefly suppression. Bloom's bright pass scales every pixel down to at most BLOOM_MAX_INPUT
// luminance before the blur chain, so one hot pixel (a specular glint, luminance ~125 in the HDR buffer) cannot grow
// into a big ball, while every real light source (lamps, windows, signs: <= ~3.3 at night) passes unchanged.
export const BLOOM_MAX_INPUT = 4.0;
// VIS-221: `cap` (a uniform, pipeline.bloom.cap; BLOOM_MAX_INPUT unless main.js lowers it by day) is that limit.
const cappedHighPass = Fn(({ input, threshold, smoothWidth, cap }) => {
  const a = smoothstep(threshold, threshold.add(smoothWidth), luminance(input.rgb));
  const c = mix(vec4(0), input, a).toConst();
  return vec4(c.rgb.mul(min(1, cap.div(max(luminance(c.rgb), 1e-4)))), c.a);
});

// ---------------------------------------------------------------- bloom in 10 passes (WS-77 lane 2R)
// three's BloomNode (the UnrealBloomPass port) draws 12 passes a frame: bright pass, 5 mips x (horizontal + vertical
// gaussian), composite; each pass is a renderer.render with its own encoder and submit (3.0-3.3 ms of a phone's JS a
// frame, baseline C). The same kernels, the same mips and the same composite in 10 passes:
//  - the bright pass (firefly cap, threshold, knee) runs on each of the 2 x 2 finish texels under a mip 0 texel before
//    averaging them (three's ran on their bilinear mean: thin lights' glow flickered on moves). It has no target of its
//    own: it draws into mip 0's vertical target, which the vertical blur then overwrites;
//  - mip 2 (1/8 of the screen) blurs in one 2D pass: the product of its two 1D kernels (the same paired bilinear taps),
//    read from mip 1, instead of a horizontal and a vertical pass;
//  - the composite (the 5 mips x their radius factors x strength) is read by the output pass itself.
// Every pass keeps its own material and texture, so no bind group is rebuilt and no uniform rewritten per pass.
// Proof (docs/qa/ws77/post/): frozen frames vs three's BloomNode on WebGPU, at most 1 level apart, 0 px over 2.
const gauss = (k) => { // BloomNode._getSeparableBlurMaterial's coefficients and paired taps for kernel radius k
  const c = [], sigma = k / 3;
  for (let i = 0; i < k; i++) c.push(0.39894 * Math.exp(-0.5 * i * i / (sigma * sigma)) / sigma);
  const off = [], w = [];
  for (let i = 1; i < k; i += 2) { const wa = c[i], wb = i + 1 < k ? c[i + 1] : 0; off.push((i * wa + (i + 1) * wb) / (wa + wb)); w.push(wa + wb); }
  return { c, off, w };
};
const BLOOM_KERNELS = [6, 10, 14, 18, 22], BLOOM_FACTORS = [1.0, 0.8, 0.6, 0.4, 0.2];
// mips that blur in one 2D pass. Only mip 2 (1/8 of the screen, ~20k pixels): at mips 3 and 4 (1.4k-5.6k pixels, 361 and
// 529 taps a pixel) a 2D pass runs latency-bound, 0.034 ms each on an RTX 5080 against 0.022 for its two 1D passes
const BLOOM_2D = [2];
const loopN = (n) => ({ start: int(0), end: int(n), type: 'int', condition: '<' });
// Phone set (PHONE.bloomMips, `plan`): mips of their own: each { div: its size as a share of the drawing buffer, kernel,
// twoD, share: how much of each of HIGH's 5 mip weights it carries }. Mip 0: a bright pass (each of its texels the mean
// bright of the div x div finish texels under it, texel by texel), then its kernel in one 2D pass; a later mip blurs the
// one above, in one 2D pass or two 1D passes.
class BloomPassesNode extends THREE.TempNode {
  static get type() { return 'BloomPassesNode'; }
  // input: the finish target's texture; pre(uv) (VIS-267 W7, the phone set's one output pass): the finished colour at uv
  // instead (there is no finish target), read wherever the bright pass reads `input`
  constructor(input, { strength, radius, threshold, smoothWidth, cap = float(BLOOM_MAX_INPUT) }, plan = null, pre = null) {
    super('vec4');
    Object.assign(this, { input, strength, radius, threshold, smoothWidth, cap, plan, pre });
    this.updateBeforeType = THREE.NodeUpdateType.FRAME;
    const rt = (name) => { const t = new THREE.RenderTarget(1, 1, { depthBuffer: false, type: THREE.HalfFloatType }); t.texture.name = name; t.texture.generateMipmaps = false; return t; };
    // per mip its targets (a horizontal and a vertical one, or one for a 2D mip; the last is the mip's result) and one texel in uv
    this.mips = plan ? plan.map((p, i) => (p.twoD ? [rt(`bloom.m${i}`)] : [rt(`bloom.h${i}`), rt(`bloom.v${i}`)]))
      : BLOOM_KERNELS.map((k, i) => (BLOOM_2D.includes(i) ? [rt(`bloom.m${i}`)] : [rt(`bloom.h${i}`), rt(`bloom.v${i}`)]));
    this.inv = this.mips.map(() => uniform(new THREE.Vector2(1, 1)));
    this.full = uniform(new THREE.Vector2(1, 1)); // one finish (drawing buffer) texel in uv (plan: mip 0's bright reads)
    this.passes = []; // [material, target, name], in draw order (setup)
    this._state = {}; // (RendererUtils' saved state: an object, never null)
  }
  setSize(w, h) { // BloomNode.setSize: mip 0 at half the drawing buffer, each next mip half the one above (floored)
    this.full.value.set(1 / Math.max(1, w), 1 / Math.max(1, h));
    if (this.plan) { // phone: each mip at its own share of the drawing buffer
      this.plan.forEach(({ div }, i) => {
        const x = Math.max(1, Math.floor(w / div)), y = Math.max(1, Math.floor(h / div));
        for (const t of this.mips[i]) t.setSize(x, y);
        this.inv[i].value.set(1 / x, 1 / y);
      });
      return;
    }
    let x = Math.max(1, Math.floor(w * 0.5)), y = Math.max(1, Math.floor(h * 0.5));
    for (let i = 0; i < this.mips.length; i++) {
      for (const t of this.mips[i]) t.setSize(x, y);
      this.inv[i].value.set(1 / x, 1 / y);
      x = Math.max(1, Math.floor(x / 2)); y = Math.max(1, Math.floor(y / 2));
    }
  }
  updateBefore({ renderer }) {
    this._state = THREE.RendererUtils.resetRendererState(renderer, this._state);
    this.setSize(...renderer.getDrawingBufferSize(_size).toArray());
    for (const [m, t, name] of this.passes) { _quad.material = m; _quad.name = name; renderer.setRenderTarget(t); _quad.render(renderer); }
    THREE.RendererUtils.restoreRendererState(renderer, this._state);
  }
  setup(builder) {
    const tex = (t) => texture(t.texture);
    if (!this.passes.length) this.passes = this.makePasses(context(builder.getSharedContext()), tex); // once: a second build of the output (another target) keeps them
    // BloomNode's composite, read by the pass that draws this node: sum of lerp(f, 1.2 - f, radius) x mip, x strength
    let sum = null;
    if (this.plan) { // phone: each mip carries its share of HIGH's 5 mip weights (the same total strength)
      const c = BLOOM_FACTORS.map((v) => { const f = float(v); return mix(f, float(1.2).sub(f), this.radius); });
      this.mips.forEach((m, i) => {
        let w = null;
        this.plan[i].share.forEach((s, j) => { if (s) { const x = s === 1 ? c[j] : c[j].mul(s); w = w ? w.add(x) : x; } });
        const t = w.mul(tex(m.at(-1))); sum = sum ? sum.add(t) : t;
      });
      return sum.mul(this.strength);
    }
    this.mips.forEach((m, i) => { const f = float(BLOOM_FACTORS[i]), c = mix(f, float(1.2).sub(f), this.radius).mul(tex(m.at(-1))); sum = sum ? sum.add(c) : c; });
    return sum.mul(this.strength);
  }
  makePasses(ctx, tex) {
    const mat = (name, node) => { const m = new THREE.NodeMaterial(); m.name = name; m.contextNode = ctx; m.fragmentNode = node; return m; };
    // the finish target's texture, not its RTT node: a material that holds the RTT node sets it up again, which marks the
    // finish material changed, so three built the finish, the AO and the AO blur twice (the second frame, synchronously)
    const src = this.pre ? null : texture(this.input);
    const hi = (u) => cappedHighPass({ input: this.pre ? this.pre(u) : src.sample(u), threshold: this.threshold, smoothWidth: this.smoothWidth, cap: this.cap }).rgb;
    // the mean bright of the n x n block of finish texels round uv u, each texel through the bright pass on its own (read
    // at its centre). Glow flicker fix (2026-10-29): the bright pass ran on bilinear 2 x 2 means, so a thin light (an LED
    // strip, a lamp a pixel or two wide) lost its glow whenever a move split it over two blocks (its mean fell under the
    // night's hard threshold, 0.92 with a 0.01 knee) and got it back a step later: the glow flickered as the camera moved
    const block = (u, n) => {
      const c = floor(u.div(this.full).add(0.5)).toConst(), b = vec3(0).toVar();
      for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) b.addAssign(hi(c.add(vec2(x - n / 2 + 0.5, y - n / 2 + 0.5)).mul(this.full)));
      return b.mul(1 / (n * n));
    };
    // BloomNode's separable blur with a fixed texture and direction
    const blur1 = (src, k, inv, dir) => {
      const g = gauss(k), O = array(g.off), W = array(g.w), D = vec2(...dir);
      return Fn(() => {
        const vUv = uv(), sum = src.sample(vUv).rgb.mul(g.c[0]).toVar();
        Loop(loopN(g.off.length), ({ i }) => {
          const o = D.mul(inv).mul(O.element(i));
          sum.addAssign(src.sample(vUv.add(o)).rgb.add(src.sample(vUv.sub(o)).rgb).mul(W.element(i)));
        });
        return vec4(sum, 1);
      })();
    };
    // the horizontal kernel x the vertical kernel in one pass (the 1D taps: the centre, then each pair at +o and -o); rows
    // in a TSL Loop, a row's taps written out (both indices dynamic ran 10x slower: weight arrays indexed per tap)
    const blur2 = (src, k, inv) => {
      const g = gauss(k), o = [0, ...g.off.flatMap((x) => [x, -x])], w = [g.c[0], ...g.w.flatMap((x) => [x, x])];
      const O = array(o), W = array(w);
      return Fn(() => {
        const vUv = uv(), sum = vec3(0).toVar();
        Loop(loopN(o.length), ({ i }) => {
          const v = vUv.add(vec2(0, O.element(i)).mul(inv)).toConst(), row = vec3(0).toVar();
          for (let j = 0; j < o.length; j++) row.addAssign(src.sample(v.add(vec2(o[j], 0).mul(inv))).rgb.mul(w[j]));
          sum.addAssign(row.mul(W.element(i)));
        });
        return vec4(sum, 1);
      })();
    };
    if (this.plan) return this.planPasses(mat, tex, block, blur1, blur2);
    const passes = [];
    this.mips.forEach((m, i) => {
      const k = BLOOM_KERNELS[i], inv = this.inv[i], prev = i && tex(this.mips[i - 1].at(-1));
      if (m.length === 1) passes.push([mat(`Bloom_m${i}`, blur2(prev, k, inv)), m[0], `Bloom [ ${i}: 2D ]`]);
      else {
        if (!i) passes.push([mat('Bloom_b0', Fn(() => vec4(block(uv(), 2), 1))()), m[1], 'Bloom [ 0: bright ]']); // into the vertical target: free until the vertical blur overwrites it
        passes.push([mat(`Bloom_h${i}`, blur1(i ? prev : tex(m[1]), k, inv, [1, 0])), m[0], `Bloom [ ${i}: horizontal ]`]);
        passes.push([mat(`Bloom_v${i}`, blur1(tex(m[0]), k, inv, [0, 1])), m[1], `Bloom [ ${i}: vertical ]`]);
      }
    });
    return passes;
  }
  planPasses(mat, tex, block, blur1, blur2) { // phone (this.plan): see the comment above the class
    const passes = [];
    this.plan.forEach((p, i) => {
      const m = this.mips[i], inv = this.inv[i];
      if (i === 0) {
        // the bright pass alone (each mip texel: the mean bright of the div x div finish texels under it, each texel on its
        // own), then the kernel in one 2D pass. The same two passes as the bright + horizontal and the vertical before,
        // and a quarter of the finished colours read (bright + horizontal read each block once per tap, 19 times)
        passes.push([mat('Bloom_b0', Fn(() => vec4(block(uv(), p.div), 1))()), m[0], 'Bloom [ 0: bright ]']);
        passes.push([mat('Bloom_m0', blur2(tex(m[0]), p.kernel, inv)), m[1], 'Bloom [ 0: 2D ]']);
        return;
      }
      const prev = tex(this.mips[i - 1].at(-1));
      if (p.twoD) passes.push([mat(`Bloom_m${i}`, blur2(prev, p.kernel, inv)), m[0], `Bloom [ ${i}: 2D ]`]);
      else {
        passes.push([mat(`Bloom_h${i}`, blur1(prev, p.kernel, inv, [1, 0])), m[0], `Bloom [ ${i}: horizontal ]`]);
        passes.push([mat(`Bloom_v${i}`, blur1(tex(m[0]), p.kernel, inv, [0, 1])), m[1], `Bloom [ ${i}: vertical ]`]);
      }
    });
    return passes;
  }
  dispose() { super.dispose(); for (const t of this.mips.flat()) t.dispose(); for (const [m] of this.passes) m.dispose(); }
}

// ---------------------------------------------------------------- Film's blend of a video frame's pictures
// Film's recording (lazy/film.js) draws each video frame as several pictures (the shutter's moments, each a fraction of
// a pixel aside) and blends them here, on the graphics card: each picture's finish (the scene with its AO, mist and
// grade: linear light, before the bloom and the tone curve) goes into one HalfFloat picture as a running average,
// picture j weighed 1 / (j + 1) by the blend constant (nothing read back, nothing cleared). The bloom and the output then
// read the blend once a video frame: a bright light moving in the shutter adds its light as a camera's film does (an
// average after the tone curve dimmed it), and the bloom and the tone curve run once a frame, not once a picture.
class BlendNode extends THREE.TempNode {
  static get type() { return 'BlendNode'; }
  constructor(src) { // src: the finish (its texture node: drawing the blend draws the finish, the AO and the scene first)
    super('vec4');
    this.src = src;
    this.updateBeforeType = THREE.NodeUpdateType.FRAME;
    this.rt = new THREE.RenderTarget(1, 1, { type: THREE.HalfFloatType, depthBuffer: false });
    this.rt.texture.name = 'film.blend';
    this.mat = new THREE.NodeMaterial(); this.mat.name = 'Film blend';
    Object.assign(this.mat, { blending: THREE.CustomBlending, blendEquation: THREE.AddEquation, blendSrc: THREE.ConstantColorFactor, blendDst: THREE.OneMinusConstantColorFactor,
      blendEquationAlpha: THREE.AddEquation, blendSrcAlpha: THREE.ConstantAlphaFactor, blendDstAlpha: THREE.OneMinusConstantAlphaFactor, depthTest: false, depthWrite: false });
    // this picture's weight: 1 / (its number in the frame + 1). (Never a one-letter name: on a node `x y z w r g b a s t p q`
    // are TSL swizzles: a field named w was an assign to the 4th channel, and every recording failed at its first
    // picture; tests/unit/runtime/node-fields.test.mjs)
    this.weight = 1;
    this._out = passTexture(this, this.rt.texture);
    this._state = {};
  }
  getTextureNode() { return this._out; }
  updateBefore({ renderer }) {
    const s = renderer.getDrawingBufferSize(_size), k = Number.isFinite(this.weight) ? Math.min(1, Math.max(0, this.weight)) : 1;
    if (this.rt.width !== s.x || this.rt.height !== s.y) this.rt.setSize(s.x, s.y);
    this.mat.blendColor.setRGB(k, k, k); this.mat.blendAlpha = k;
    this._state = THREE.RendererUtils.resetRendererState(renderer, this._state);
    renderer.autoClear = false; // (the running average reads what the frame's earlier pictures left)
    _quad.material = this.mat; _quad.name = 'Film blend';
    try { renderer.setRenderTarget(this.rt); _quad.render(renderer); } finally { THREE.RendererUtils.restoreRendererState(renderer, this._state); }
  }
  setup() {
    this.mat.fragmentNode = vec4(this.src);
    this.mat.needsUpdate = true;
    return this._out;
  }
  dispose() { super.dispose(); this.rt.dispose(); this.mat.dispose(); }
}

// ---------------------------------------------------------------- the canvas without a depth buffer (VIS-267)
// The canvas holds only the output pass: one full-screen quad that tests nothing against depth. three gives the canvas
// a depth buffer of its size anyway (WebGPUTextureUtils.getDepthBuffer, 'depthBuffer'), cleared every picture and never
// read: 2.8 MB on a phone (585 x 1266), 23 MB on a 2x laptop screen (the canvas at CANVAS_DPR). On WebGPU the canvas
// draws without one (renderer.depth false: no depth attachment, the output's pipeline without a depth state; the
// pictures are the same, nothing reads it). Every compile keeps today's depth: three's compileAsync gives the context
// it compiles for renderer.depth whatever its target (Renderer.compileAsync), and a scene compiled without it failed
// ("Depth stencil state is not present": the old "canvas without depth" try), so a compile into a target runs with that
// target's own depth, as its draw does (three: renderContext.depth = renderTarget.depthBuffer; VIS-267: always on, the
// light map's 7 warm pipelines had a depth its draws don't and were all built again, sync, at its first draw). No engine
// material writes depth from a node (NodeMaterial.setupDepth: no depthNode, MRT depth or logarithmic depth), so no
// program reads the flag. The WebGL2 fallback keeps it: its canvas depth is the context's own.
export function canvasWithoutDepth(renderer) {
  if (!renderer?.backend?.isWebGPUBackend || renderer.depth === false || typeof renderer.compileAsync !== 'function') return false;
  const compile = renderer.compileAsync;
  renderer.compileAsync = function (...a) { // (it reads renderer.depth before its first await)
    const d = this.depth, t = this.getRenderTarget();
    if (t !== null) this.depth = t.depthBuffer;
    try { return compile.apply(this, a); } finally { this.depth = d; }
  };
  renderer.depth = false;
  renderer.backend.updateSize?.(); // (a canvas pass made before now keeps its descriptor: made again, without depth)
  return true;
}

// ---------------------------------------------------------------- pipeline
// WS-63 owner fix: aoBias (sine of the gentlest fold that darkens; 0.08 = today) > 0.08 keeps the depth AO off the terrain
// mesh's gentle folds (straight lines along its 6 m grid on calm snow); the strong contacts keep their depth (aoK).
export function createPipeline({ renderer, scene, camera, focus: focusCfg = {}, tier, aoBias, rescue = false, phone = false }) {
  // WS-72 step 5: one quality. `forced` only echoes what was asked for (a preset's render.quality, ?quality=, a pin), so
  // the settings that report it back (main.js screen()) keep round-tripping; it never changes the picture.
  const asked = (n) => (NAMES.includes(n) && n !== 'auto' ? n : null);
  let forced = asked(tier === undefined ? new URLSearchParams(location.search).get('quality') : tier);
  const tierName = 'high', T = rescue ? RESCUE : phone ? PHONE : HIGH; // WS-77: one table (?tiers= is ignored); VIS-239: the rescue set; the phone set (main.js PHONE_ON)

  // Tone mapping: ACES. Side-by-side at day and blue hour (JOURNAL 2026-10-11): AgX greys out the twilight
  // palette that sun.js is keyed for; Neutral keeps hues truer but snow highlights clip harder. ?tm= to compare.
  const TM = { agx: THREE.AgXToneMapping, aces: THREE.ACESFilmicToneMapping, neutral: THREE.NeutralToneMapping };
  renderer.toneMapping = TM[qa('tm')] ?? THREE.ACESFilmicToneMapping;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  const aoParam = qa('ao'); // ?ao=0|1 overrides the tier (A/B checks); ?ao=show draws the AO alone
  const aoOn = aoParam ? aoParam !== '0' : T.ao;

  // the finishing uniforms (uniform nodes: ext.frame(u) sets their .value)
  const u = {
    texel: uniform(new THREE.Vector2(1, 1)), aoTexel: uniform(new THREE.Vector2(1, 1)), projInv: uniform(camera.projectionMatrixInverse),
    aoStrength: uniform(aoOn ? 0.85 : 0), aoShow: uniform(aoParam === 'show' ? 1 : 0), dof: uniform(0), focusZ: uniform(100), focusRange: uniform(12),
    vignette: uniform(0.1), dofMaxPx: uniform(7),
    grade: uniform(new THREE.Vector3(1, 1, 1)), // WS-42: (saturation, contrast, the night's dark-tone power) in linear HDR before tone mapping; (1, 1, 1) = off (winter by day)
    wb: uniform(new THREE.Vector3(1, 1, 1)), // picture parameter warmth: white balance, luminance kept; (1, 1, 1) = off
    liftFade: uniform(new THREE.Vector2(1e9, 2e9)), // the night's lift fades out between these view depths (m; main.js seasonLight)
    up: uniform(0), sharp: uniform(Number(qa('sharp')) >= 0 && qa('sharp') !== null ? Number(qa('sharp')) : 1), // WS-78 lane C: upscale on (the chain draws below the canvas's density), RCAS strength (?sharp=0..1, QA)
  };
  const grade = u.grade.value, wb = u.wb.value;
  let ext = null; // WS-63 atmos: lazy/atmos.js, see extend()
  // WS-78 lane C: the 3D chain (scene, AO, finish, bloom) draws at `inner` device pixels per CSS pixel (HIGH.dpr, 1.5);
  // the canvas and the output pass at the screen's own density (up to CANVAS_DPR). Each chain node draws with the
  // canvas's pixel ratio set to `inner` for its own update, so everything sized by the drawing buffer or the pixel
  // ratio inside the chain (targets, three's screenDPR for point sprites) agrees with the size it draws at.
  let inner = 1;
  const atInner = (node) => {
    const f = node.updateBefore;
    node.updateBefore = function (frame) {
      const ct = canvasTarget(frame.renderer), pr = ct?._pixelRatio;
      if (!ct || inner === pr) return f.call(this, frame); // (VIS-267 W7: above the canvas's too, while a raised level has not yet held)
      ct._pixelRatio = inner;
      try { return f.call(this, frame); } finally { ct._pixelRatio = pr; restoreViewport(frame.renderer); } // VIS-227: the WebGL2 backend's restore of the open pass's viewport read the chain's ratio (a 75 % picture at DPR 2)
    };
    return node;
  };

  // (the default depth texture, for the device fallback: a 32-bit float one broke the WebGL2 backend's MSAA depth
  // resolve, glBlitFramebuffer refuses the format pair, and the fallback drew black)
  const scenePass = pass(scene, camera, { samples: T.samples, type: THREE.HalfFloatType });
  scenePass.name = 'scene';
  atInner(scenePass); // WS-78 lane C
  // WS-78 lane C: the 4x colour samples are not written back after the resolve (nothing reads them: no transmission or
  // viewport-texture reads of the scene target); the multisampled depth stays stored (AO and depth of field read it)
  scenePass.renderTarget.storeMultisampledColorBuffer = false;
  const colorTex = scenePass.getTextureNode('output'), depthTex = scenePass.getTextureNode('depth');
  const aoReuse = qa('aoreuse') !== null ? Math.max(0, Number(qa('aoreuse')) || 0) : T.aoReuse || 0; // phone: frames the AO is kept at most (?aoreuse=0: every frame)
  const aoNode = aoOn ? atInner(new HalfAONode(depthTex, camera, { samples: T.aoSamples, reuse: aoReuse })) : null;
  if (aoNode && aoBias) { aoNode.bias.value = aoBias; aoNode.aoK.value = 0.92 / (1 - aoBias); } // WS-63 owner fix

  const viewZ = (q) => getViewPosition(q, depthTex.sample(q).x, u.projInv).z;
  // the per-pixel end of the finish (in a Fn): the season grade, the split tone, the warmth and the vignette on col (a
  // vec4 var) at screen uv vUv. The finish ends with it; the phone set's bloom also runs it on each colour it reads.
  function pointFinish(col, vUv, X) {
    If(u.grade.x.notEqual(1).or(u.grade.y.notEqual(1)).or(u.grade.z.notEqual(1)), () => { // WS-42: per-season grade; contrast pivots on mid grey, nothing below 0
      const lit = max(col.rgb, vec3(1e-6)).div(0.18).toConst();
      // the night's lift (z < 1): a brightness under mid grey raised by a power, one above it left as it is; on the
      // luminance, so the colour is kept (per channel it greyed the night, 2026-10-28); not the sky (nothing drawn
      // there writes depth), and fading out with distance (u.liftFade), so the far land meets the sky unlifted
      const k0 = X.depth ? mix(u.grade.z, 1, step(1, X.depth.sample(vUv).x)) : u.grade.z;
      const k = X.z ? mix(k0, 1, smoothstep(u.liftFade.x, u.liftFade.y, X.z.negate())) : k0;
      const L = max(luminance(lit), 1e-6).toConst();
      const l = lit.mul(pow(min(L, 1), k).add(max(L.sub(1), 0)).div(L));
      const c = pow(l, vec3(u.grade.y)).mul(0.18).toConst();
      col.rgb.assign(max(mix(vec3(luminance(c)), c, u.grade.x), vec3(0)));
    });
    ext?.split?.(X); // WS-63 atmos: split tone (before the warmth)
    col.rgb.mulAssign(u.wb);
    const q = vUv.sub(0.5);
    col.rgb.mulAssign(mix(1, smoothstep(0.95, 0.25, length(q.mul(vec2(1.0, 0.8)))), u.vignette));
    return col;
  }
  // the finish of one pixel (in a Fn): col (a vec4 var: the scene's colour, safe()) at screen uv vUv; dofMix (a float
  // var, optional): set to the depth of field's mix at this pixel
  function finishOf(col, vUv, dofMix = null) {
    const z = viewZ(vUv).toConst();
    if (aoNode) {
      // Depth-aware upsample of the half-res AO: neighbours across a depth edge don't bleed.
      const aoTex = aoNode.getTextureNode();
      const sum = float(0).toVar(), wsum = float(0).toVar();
      for (let i = 0; i < 4; i++) {
        const o = vec2(i === 0 || i === 2 ? -1 : 1, i < 2 ? 1 : -1).mul(u.aoTexel); // (y flipped: screen up = -uv.y)
        const w = float(1).div(float(0.02).add(abs(viewZ(vUv.add(o)).sub(z)).div(max(1, z.negate().mul(0.02)))));
        sum.addAssign(aoTex.sample(vUv.add(o)).r.mul(w)); wsum.addAssign(w);
      }
      const ao = sum.div(wsum).toConst();
      // Light sources (windows, lamps, fire) don't get occluded: AO fades out above ~1.0 luminance.
      const lum = luminance(col.rgb);
      col.rgb.mulAssign(mix(1, ao, u.aoStrength.mul(float(1).sub(smoothstep(0.8, 2.0, lum)))));
      If(u.aoShow.greaterThan(0.5), () => { col.rgb.assign(vec3(ao)); });
    }
    if (T.dof) {
      // Circle of confusion grows away from the focused place; a golden-angle disc gathers the blur. Every pixel
      // gathers while a place is focused (a uniform branch); the mix is 0 below coc 0.02, as the old per-pixel test.
      If(u.dof.greaterThan(0.001), () => {
        const coc = clamp(abs(z.negate().sub(u.focusZ)).sub(u.focusRange).div(u.focusZ.mul(0.9).add(1)), 0, 1).mul(u.dof).toConst();
        const acc = col.rgb.toVar();
        for (let i = 1; i < 16; i++) {
          const a = i * 2.39996, rr = coc.mul(Math.sqrt(i / 16)).mul(u.dofMaxPx);
          acc.addAssign(safe(colorTex.sample(vUv.add(vec2(Math.cos(a), -Math.sin(a)).mul(rr).mul(u.texel)))).rgb);
        }
        const m = smoothstep(0.02, 0.25, coc);
        col.rgb.assign(mix(col.rgb, acc.div(16), m));
        dofMix?.assign(m);
      });
    }
    const X = { col, z, uv: vUv, depth: depthTex, projInv: u.projInv, texel: u.texel, u };
    ext?.air?.(X); // WS-63 atmos: mist, aerial perspective (before the grade)
    return pointFinish(col, vUv, X);
  }
  function finishNode() {
    return Fn(() => {
      const vUv = uv();
      return finishOf(safe(colorTex.sample(vUv)).toVar(), vUv);
    })();
  }

  const bloomFx = T.bloom ? { strength: uniform(0.55), radius: uniform(0.6), threshold: uniform(0.92), smoothWidth: uniform(0.01), cap: uniform(BLOOM_MAX_INPUT) } : null;
  let finishTex = null, bloomPass = null;
  // WS-78 lane C: the output upscales the finish picture to the canvas with a contrast-adaptive sharpen (AMD FSR1 RCAS:
  // the centre and its 4 neighbours one finish texel away, the lobe limited by the local min / max so nothing clips),
  // in a tone-mapped space (c / (1 + c)) and clamped to the 5 taps' range (no halo, no ringing). u.up = 0 when the chain
  // draws at the canvas's own size: the picture passes through unchanged (phone set, T.sharpen: it sharpens at 1:1).
  // (in a Fn) c: the centre's colour (a vec3 var), tap(uv): a neighbour's colour (vec3), k (optional): the lobe's share
  function sharpen(c, tap, k = null) {
    If(u.up.greaterThan(0.5), () => {
      const vUv = uv(), tx = vec2(u.texel.x, 0), ty = vec2(0, u.texel.y);
      const tm = (v) => { const q = max(v, vec3(0)); return q.div(q.add(1)); };
      const e = tm(c), n = tm(tap(vUv.sub(ty))), s = tm(tap(vUv.add(ty)));
      const l = tm(tap(vUv.sub(tx))), r = tm(tap(vUv.add(tx)));
      const mn = min(min(n, s), min(l, r)).toConst(), mx = max(max(n, s), max(l, r)).toConst();
      const hitMin = mn.div(mx.mul(4).max(1e-5)), hitMax = vec3(1).sub(mx).div(mn.mul(4).sub(4).min(-1e-5));
      const lr = max(hitMin.negate(), hitMax);
      const lobe = max(-0.1875, min(max(lr.x, max(lr.y, lr.z)), 0)).mul(k ? u.sharp.mul(k) : u.sharp).toConst();
      const y = n.add(s).add(l).add(r).mul(lobe).add(e).div(lobe.mul(4).add(1));
      const yc = clamp(y, min(mn, e), max(mx, e)).min(vec3(0.9999)).toConst();
      c.assign(yc.div(vec3(1).sub(yc)));
    });
  }
  function upscaled(fin) {
    const tex = fin.renderTarget.texture;
    return Fn(() => {
      const c = vec3(fin.rgb).toVar();
      sharpen(c, (q) => texture(tex, q).rgb);
      return vec4(c, fin.a);
    })();
  }
  // VIS-267 W7 (T.oneOutput: the phone and rescue sets): one output pass draws the finish too. The
  // centre pixel is finished in full; the sharpen's neighbours and the bloom's bright reads go through finishLite (the
  // AO from its blurred half-size picture read where they read; the per-pixel finish; no depth of field, which only a
  // resort's selection blur turns on: where it blurs, the sharpen fades by its mix, else the sharp neighbours undo the
  // blur; the old chain sharpened the blurred picture, which changed little there).
  function oneOutputNode() {
    // (the targets' textures, not their pass nodes: see BloomPassesNode.makePasses)
    const sceneT = texture(scenePass.renderTarget.texture), aoT = aoNode ? texture(aoNode.blurRT.texture) : null;
    const finishLite = (q) => { // (in a Fn) the finished colour at uv q, without depth: the AO read bilinear, no DOF
      const col = safe(sceneT.sample(q)).toVar();
      if (aoT) col.rgb.mulAssign(mix(1, aoT.sample(q).r, u.aoStrength.mul(float(1).sub(smoothstep(0.8, 2.0, luminance(col.rgb))))));
      return pointFinish(col, q, { col, uv: q, u }); // (the split tone reads X.col only)
    };
    const out = Fn(() => { // the centre finished in full, its 4 sharpen neighbours through finishLite
      const vUv = uv(), dm = float(0).toVar(), col = finishOf(safe(colorTex.sample(vUv)).toVar(), vUv, dm), c = col.rgb.toVar();
      sharpen(c, (q) => finishLite(q).rgb, float(1).sub(dm));
      col.rgb.assign(c);
      return col;
    })();
    if (!bloomFx) return out;
    bloomPass = atInner(new BloomPassesNode(null, bloomFx, T.bloomMips || null, finishLite)); // drawn after the AO (the output reads the AO first)
    return out.add(bloomPass);
  }
  // Film's blend (BlendNode, api.blend): on = the output reads the blend of a video frame's pictures; `pipe` draws a
  // picture into the blend alone (scene, AO, finish, blend: no bloom, no output transform), every picture of a frame but its last
  const B = { on: false, node: null, pipe: null, last: true };
  function outputNode() {
    const was = [bloomPass, B.node]; // (the nodes this build replaces: their targets freed once the new output is set)
    queueMicrotask(() => { for (const n of was) if (n && n !== bloomPass && n !== B.node) n.dispose(); });
    B.node = null;
    if (T.oneOutput && !B.on) { finishTex = null; return oneOutputNode(); } // (a blend needs the finish in a target of its own, the phone sets too)
    finishTex = atInner(convertToTexture(finishNode(), null, null, { depthBuffer: false })); // rendered once a frame; bloom and the output read it (WS-75 perf: a full-screen pass needs no depth buffer, 5.5 MB at 1600x900)
    finishTex.name = 'finish';
    if (B.on) B.node = atInner(new BlendNode(finishTex));
    let out = B.on ? vec4(B.node.getTextureNode()) : upscaled(finishTex); // WS-78 lane C (reads finishTex itself: its update draws the chain); a capture draws at full density, nothing to sharpen
    if (bloomFx) {
      bloomPass = atInner(new BloomPassesNode((B.node?.rt ?? finishTex.renderTarget).texture, bloomFx, T.bloomMips || null)); // WS-77 2R: three's BloomNode in 10 passes (above; the phone set: 3, PHONE.bloomMips); drawn after the finish (the output reads finishTex first)
      out = out.add(bloomPass);
    }
    return out;
  }
  // main.js and QA read and set pipeline.bloom.strength / .threshold / .radius / .smoothWidth (the bright pass's knee)
  const bloomApi = bloomFx && {
    get strength() { return bloomFx.strength.value; }, set strength(v) { bloomFx.strength.value = v; },
    get threshold() { return bloomFx.threshold.value; }, set threshold(v) { bloomFx.threshold.value = v; },
    get radius() { return bloomFx.radius.value; }, set radius(v) { bloomFx.radius.value = v; },
    get smoothWidth() { return bloomFx.smoothWidth.value; }, set smoothWidth(v) { bloomFx.smoothWidth.value = v; },
    get cap() { return bloomFx.cap.value; }, set cap(v) { bloomFx.cap.value = v; }, // VIS-221: the bright pass's per-pixel limit
  };
  sharedInstancePrograms(); // WS-75 perf: instanced meshes of one material share a pipeline (gpu/pipelines.js)
  sharedValueBuilds(renderer); // WS-75 perf: materials that differ only in values (metalness 0 / 0.1 ...) share a node build
  parallelPipelines(renderer); // WS-75 perf (VIS-267 W6: from the start, so a bake's own warm (lazy/lightcast.js) compiles side by side too)
  batchPipelines(warmBatchFor(renderer.backend)); // VIS-267: batches of 4 on the WebGL2 backend; WebGPU starts every pipeline at once (gpu/pipelines.js)
  skipDarkLights(); // iGPU lane: lights at intensity 0 (the moon, the fires by day) cost nothing (gpu/lighting.js)
  sharedSamplers(); // VIS-272: a lit material stays within 16 samplers a stage (gpu/samplers.js)
  watchPrograms(renderer, { report: (what, err, facts) => degraded('drawing', err, { what, ...facts }) }); // three skips a failed program for good (gpu/programs.js; facts.why = three's reason, the Sentry tag `why`)
  canvasWithoutDepth(renderer); // VIS-267: the canvas's output quad needs no depth buffer (above)
  const pipeline = new THREE.RenderPipeline(renderer);
  pipeline.outputNode = outputNode();
  // WS-78 lane C (HIGH.dpr). QA / captures: ?dpr=<n> draws at up to n device pixels per CSS pixel (e.g. ?dpr=2 for a
  // website still taken at 2x); it never changes a visitor's map. It also lifts the pixel limit's start (T.px, setInner):
  // the density is exactly n (dprQ: n, else 0)
  const dprQ = Math.min(+qa('dpr'), 3) || 0, dprCap = dprQ > 0 ? dprQ : T.dpr;
  const canvasRatio = () => Math.min(globalThis.devicePixelRatio || 1, Math.max(T.canvas ?? CANVAS_DPR, dprCap)); // the canvas + output (VIS-239: 1 in the rescue set; the phone set: 1.5, the picture's own)
  renderer.setPixelRatio(canvasRatio());
  // VIS-267 W7 (T.follow, the phone set): the density level the canvas shows (Infinity: canvasRatio(), the top); set
  // only by follow(), right before a draw. top(): the ceiling of the 3D chain's density (the canvas's own elsewhere, and
  // during a capture: fullDensity draws the 3D chain at the capture's own drawing-buffer size, never above it).
  let shown = Infinity;
  const canvasWant = () => Math.min(canvasRatio(), shown);
  const top = () => (T.follow && !AD.fullOn ? canvasRatio() : renderer.getPixelRatio());

  // WS-78 lane C: adaptive density. Only when the measured frame time stays over budget: the 3D chain steps down from
  // its start (the 1.5 cap, or the pixel limit's: setInner) by 0.25, never under a floor of 0.75 device px per CSS px
  // (the sharpened upscale keeps it crisp), and up to the 1.5 cap when
  // the frames hold the screen's rate again. Rarely and never back and forth: a step is taken after 1.5 s of slow
  // frames (median of the last 45 full-rate frames' work over 1000 / 38 ms; the interval between frames on WebGL2); a
  // step down that did not make the frames at least 10 % faster (the device is not held back by its graphics chip) is
  // undone and no further step down is taken;
  // a step up comes after 8 s at the screen's rate (median under 1000 / 55 ms, scaled to the next level's pixels too:
  // densityVerdict), and a level that was too slow after a
  // step up is never tried again in this visit. A fast graphics chip never meets the budget line, so nothing changes.
  // Frames count only from the map's own loop at full rate once the opening flight has landed (main.js passes busy()
  // && !introQ), and the first 3 s of them only warm up; ?dpr= (a fixed density), ?adapt=0
  // and automated browsers (navigator.webdriver: QA measures a fixed picture; ?adapt=1 turns it on there) leave it off.
  // The numbers above are HIGH's (ADAPT); the phone set judges the whole frame's work and the pace the screen got for 30
  // pictures a second (densityLines), steps to a floor of 1, and is never locked for the visit (PHONE.adapt).
  const AP = { ...ADAPT, ...T.adapt };
  const queue = renderer.backend?.device?.queue; // WebGPU: onSubmittedWorkDone tells when the GPU finished a frame
  // AD.level: the adaptive density's level (Infinity: the top); unset until its first step, which takes over from the
  // pixel limit's start (setInner)
  const AD = { on: qa('adapt') !== null ? qa('adapt') !== '0' : !globalThis.navigator?.webdriver && !(dprQ > 0),
    at: 0, t0: 0, full: false, fullOn: false, s: [], iv: [], changed: 0, check: 0, checkBy: 'work', raised: 0, noLower: false, holdDown: 0, failed: new Map(), w: 1, h: 1, log: [],
    gpu: !!(AP.gpu && typeof queue?.onSubmittedWorkDone === 'function'), measure: false, gen: 0, inFlight: 0, capHz: 0, fixed: 0, onBefore: false,
    sharp: false, woke: 0, low: 0, no075: false, info: null, told: null }; // VIS-267 round 2 C (sharpRule, sharp)
  let SLOW = AD.gpu ? AP.slow : AP.slowFrame ?? AP.slow, FAST = AD.gpu ? AP.fast : AP.fastFrame ?? AP.fast, LATE = 0;
  const FLOOR = AP.floor; // floor: the owner judged ?dpr=0.75 "pretty good from all angles" (2026-10-19)
  // a capped loop (render()'s capHz: the phone clock, rest.js PHONE_PACE.cap) moves the lines with its cap (capLines)
  function capLines(capHz) {
    if (!(capHz > 0) || capHz === AD.capHz) return;
    AD.capHz = capHz;
    ({ slow: SLOW, fast: FAST, late: LATE } = densityLines(capHz, AD.gpu));
  }
  const capNow = () => Math.min(top(), dprCap);
  function setLevel(v, now, why) {
    AD.level = v >= capNow() - 1e-6 ? Infinity : v; AD.changed = now; AD.s.length = 0; AD.iv.length = 0; AD.gen++;
    AD.log.push([Math.round(now / 100) / 10, why, Math.min(v, capNow())]); if (AD.log.length > 20) AD.log.shift();
    setInner(); // VIS-255: the 3D chain only, never the canvas (see setSize)
  }
  function sample(ms, now) { AD.s.push(ms); if (AD.s.length > AP.n) AD.s.shift(); judge(now); }
  function adapt(full) {
    const now = performance.now(), iv = now - AD.at, ok = full && AD.full && iv < 250;
    AD.at = now; AD.full = full; AD.measure = false;
    if (!AD.on || !ok) return;
    if (!AD.t0) AD.t0 = now;
    if (now - AD.t0 < 3000) return; // the first frames build pipelines and stream models
    if (AD.gpu) { AD.measure = true; if (LATE > 0) { AD.iv.push(iv); if (AD.iv.length > AP.n) AD.iv.shift(); } } // phone: render() times this frame's work (workDone); the pace the screen got
    else sample(iv, now);
  }
  // phone (AP.gpu): this frame's work = the frame's start (the animation callback, before the world step: render()'s
  // `at`) to the GPU finishing every pass it submitted (a GPU behind its frames shows here too): the whole chain that
  // decides whether a picture makes its refresh. At most 3 frames in flight; a sample from before a density change is dropped.
  function workDone(t0) {
    if (!AD.measure || AD.inFlight > 2) return;
    const gen = AD.gen; AD.inFlight++;
    queue.onSubmittedWorkDone().then(() => { const now = performance.now(); if (gen === AD.gen) sample(now - t0, now); },
      () => { /* errors:ignore: a lost device is the frame guard's (rescue.js); the density just stops judging */ }).finally(() => { AD.inFlight--; });
  }
  // VIS-271 B7: the median of the samples, sorted in one kept array (was a new array sorted per sample: per frame on phones)
  const sorted = [], byValue = (a, b) => a - b;
  const medianOf = (s) => { sorted.length = s.length; for (let i = 0; i < s.length; i++) sorted[i] = s[i]; sorted.sort(byValue); return sorted[AP.n >> 1]; };
  function judge(now) {
    if (AD.s.length < AP.n || (LATE > 0 && AD.iv.length < AP.n) || now - AD.changed < AP.wait) return;
    const step = densityVerdict(AD, { work: medianOf(AD.s), pace: LATE > 0 ? medianOf(AD.iv) : 0 }, { slow: SLOW, fast: FAST, late: LATE }, inner, capNow(), now, AP);
    if (step) { setLevel(step[0], now, step[1]); AD.told?.(step[1]); }
  }

  // VIS-255: only a real resize touches the canvas (main.js resize() draws at once: a resized canvas is blank until the
  // next draw). A density step (setLevel) changes the 3D chain alone (setInner; its targets take the new size at the
  // next draw). It used to set the canvas's size again (the same numbers) and drop three's canvas state, and on the
  // phone set that happens when the graphics chip reports a frame done, outside any frame, while the map may rest:
  // whether a browser then shows a blank canvas until the next picture is its own choice (Chrome keeps the picture).
  // The phone set's canvas follows a density level only in follow(), inside render() right before the draw.
  function setSize(w, h) {
    if (renderer.getPixelRatio() !== canvasWant()) renderer.setPixelRatio(canvasWant()); // WS-78: the window moved to another screen or the page was zoomed (the resize observer calls this)
    renderer.setSize(w, h, false);
    AD.w = w; AD.h = h;
    setInner();
  }
  // VIS-267 W7 (T.follow): the canvas takes the density of a level that has held: the step's own verdict is in (AD.check
  // 0) and FOLLOW_MS passed since it, so a step that is undone never resizes the canvas. Called by render() right before
  // the draw: the resized canvas gets its picture in the same frame (VIS-255). The parts run's fixed density keeps the
  // canvas at the top. A capture (fullDensity: the Renders tab; Film's recording, whose steps draw through render()) has
  // set the drawing buffer to its own size: the canvas is left as it is until the capture gives it back.
  // VIS-267 round 2 C: a resting map's sharp picture (AD.sharp) takes the top at once; after it the canvas keeps the top
  // FOLLOW_MS more (a short touch never resizes it twice)
  function follow(now) {
    if (!T.follow || AD.fullOn) return;
    const want = AD.fixed > 0 || AD.sharp ? Infinity : AD.level ?? Infinity; // (the pixel limit's start: the canvas at the top)
    if (want === shown || (!AD.sharp && (AD.check || now - Math.max(AD.changed, AD.woke) < FOLLOW_MS))) return;
    shown = want;
    if (renderer.getPixelRatio() !== canvasWant()) { renderer.setPixelRatio(canvasWant()); setInner(); }
  }
  // The pixel limit (T.px, the owner's pick 2026-11-03): until the adaptive density's first step the 3D chain starts at
  // sqrt(px / the CSS area) (iMac 24: 1.25, A16 iPad: 1.02), never under the floor, and ?dpr=n keeps n exactly; a
  // resize recomputes it. The adaptive top stays T.dpr: a fast GPU climbs back with the usual step up.
  // VIS-267 round 2 C (lazy/sharp.js): a still phone draws sharp = the start or the level, whichever is higher (an A16
  // iPad still at its 1.02, not the top 1.25; a phone at its 1.25)
  const startAt = () => Math.max(FLOOR, dprQ, (T.px / AD.w / AD.h) ** 0.5);
  function setInner() {
    const w = AD.w, h = AD.h;
    inner = AD.fixed > 0 ? Math.min(top(), AD.fixed) // VIS-267 W4: the parts run's fixed density (testParts().density)
      : AD.fullOn ? top() : Math.min(top(), dprCap, AD.sharp ? Math.max(AD.level ?? 0, startAt()) : AD.level ?? startAt()); // WS-78 lane C: the 3D chain's density (atInner; adapt(); fullDensity())
    u.up.value = inner < renderer.getPixelRatio() || T.sharpen ? 1 : 0; // (the phone set sharpens at 1:1 too)
    const pr = inner, W = Math.max(1, Math.floor(w * pr)), H = Math.max(1, Math.floor(h * pr));
    u.texel.value.set(1 / W, 1 / H);
    const s = capture.on ? 0 : 1; u.aoTexel.value.set(1 / Math.max(1, W >> s), 1 / Math.max(1, H >> s)); // (HalfAONode.setSize: full size in a capture)
  }

  // Selection depth of field: focus distance follows the selected place while the camera moves.
  // WS-15: strength is config (resort.json -> render.selectionFocus.blur, 0 = off .. 1 = the original look).
  // It scales the circle of confusion, so both the blur radius and its mix shrink. Default 0 (owner, 2026-10-19:
  // a selection highlights, it never blurs the map); a resort opts in with its own approved value.
  const blur = THREE.MathUtils.clamp(Number.isFinite(focusCfg?.blur) ? focusCfg.blur : 0, 0, 1);
  const focus = { point: null, k: 0, v: new THREE.Vector3() };
  function updateFocus(dt) {
    const want = focus.point && T.dof ? blur : 0;
    focus.k += (want - focus.k) * Math.min(1, dt * 3);
    u.dof.value = focus.k < 0.01 ? 0 : focus.k;
    if (focus.point) {
      const z = focus.v.copy(focus.point).applyMatrix4(camera.matrixWorldInverse).z;
      u.focusZ.value = Math.max(1, -z);
      u.focusRange.value = Math.max(10, -z * 0.18);
    }
  }

  // WS-77 2R: the post chain's own programs (AO, AO blur, finish, the bloom passes, the output) in the warm start. They
  // exist only once the output node is built, which three does at the first draw: there they were made one by one,
  // synchronously, in the first picture's frame. Here the chain is drawn once over an empty scene
  // with every new pipeline started asynchronously (three skips a draw whose pipeline is still compiling, so nothing is
  // drawn; the canvas is under the loader), in the contexts the real frame uses; the warm start waits for them with the rest.
  let postWarm = false;
  const EMPTY = new THREE.Scene();
  function warmPost() {
    const P = pipelines(renderer);
    if (!P) return;
    const upd = Object.hasOwn(P, 'updateForRender') ? P.updateForRender : null, mine = [];
    P.updateForRender = function (ro) { return this.getForRender(ro, mine); }; // (gpu/pipelines.js parallelPipelines collects them in pending())
    scenePass.scene = EMPTY;
    try { api.renderOnce(); } finally {
      scenePass.scene = scene;
      if (upd) P.updateForRender = upd; else delete P.updateForRender;
    }
  }

  const gpu = gpuName(renderer);
  const api = {
    get tier() { return tierName; },
    get config() { return T; },
    get bloom() { return bloomApi; },
    get forced() { return forced; },
    gpu,
    detected: 'high',
    setSize,
    // WS-72 step 5: the old tier calls (WS-4 control, WS-49 render.quality) are accepted and change nothing
    setTier(name) { forced = asked(name) || forced; },
    clearForced() { forced = null; },
    pin(name) { forced = asked(name); },
    onForce: null,
    onTier() {},
    focusOn(point) { focus.point = point ? point.clone() : null; },
    /** WS-42: post grade per season (resort.grade.saturation / contrast); 1 = unchanged. Runtime (live season switch).
     *  lift (0 = none, main.js grade.night.lift x darkness): a brightness under mid grey raised by the power 1 - lift,
     *  fading out from view depth liftNear to liftFar (m). */
    setGrade({ saturation = 1, contrast = 1, warmth = 1, lift = 0, liftNear = 1e9, liftFar = 2e9 } = {}) {
      grade.set(saturation, contrast, 1 - Math.min(0.8, Math.max(0, lift)));
      u.liftFade.value.set(liftNear, Math.max(liftFar, liftNear + 1));
      // warmth > 1 = warmer (more red, less blue), < 1 = cooler; scaled so a grey keeps its luminance
      const w = Math.max(0.5, Math.min(1.5, warmth)), r = w, b = 1 / w, g = 1 + (w - 1) * 0.25;
      wb.set(r, g, b).multiplyScalar(1 / (0.2126 * r + 0.7152 * g + 0.0722 * b));
    },
    get selectionBlur() { return blur; }, // WS-15
    /** WS-63 atmos (WS-75 TSL form): e = { air?(X), split?(X), frame?(u, camera) }. air runs before the season grade, split
     *  before the warmth; each changes X.col (a vec4 var) with TSL, reading X.z (view z), X.uv, X.depth, X.projInv,
     *  X.texel and X.u (the finishing uniforms). frame(u, camera) runs every frame before the draw. The output is rebuilt. */
    extend(e) { ext = e; pipeline.outputNode = outputNode(); pipeline.needsUpdate = true; },
    aoBusy(fn) { if (aoNode) aoNode.busy = fn; }, // the phone set's kept AO: fn() true = something on screen moves, the AO is drawn again this picture
    uniforms: u,
    render(dt, viewDist = 200, full = false, capHz, at, room = false, judge = full) { // full: the map's loop runs at full rate (main.js busy(); WS-78 adapt()); capHz: that loop's cap (the phone clock's 30); at: the frame's start (the animation callback's time, before the world step); room: a seat inside a building (main.js): no AO
      capLines(capHz);
      adapt(judge); // VIS-267: judge = full once the opening flight has landed (main.js); wrappers (lazy/fieldspeed.js) read `full`
      // AO radius follows the zoom: ~1.5 m around a cabin close-up, up to 7 m over the whole village. In a room the AO is
      // off (not drawn): its samples span up to 0.06 of the screen whatever the radius, so 1-2 m from a wall every inner
      // corner of a 3.5 m sauna got a wide dark band (a 0.35 m radius drew the same); a baked interior has its own shade.
      if (aoNode) { aoNode.radius.value = THREE.MathUtils.clamp(viewDist * 0.016, 1.5, 7); aoNode.off = room; u.aoStrength.value = room ? 0 : 0.85; }
      updateFocus(dt);
      ext?.frame?.(u, camera);
      const t0 = performance.now();
      follow(t0); // VIS-267 W7: the phone set's canvas takes a held density level, right before this draw
      (B.on && !B.last ? B.pipe : pipeline).render(); // Film's blend: a frame's pictures before its last go into the blend alone
      if (AD.gpu) workDone(at <= t0 && t0 - at < 250 ? at : t0); // phone: the frame's work from its start, for the density rule
    },
    // outside the animation loop (a resize, a cover still, a preset's card picture, QA): WebGPURenderer runs the passes
    // (scene, AO, finish, bloom) once per node frame, which only its own loop advances: start a new one first
    renderOnce() { nodes(renderer)?.nodeFrame.update(); ext?.frame?.(u, camera); pipeline.render(); },
    /** VIS-265: a new node frame, so the next `render` runs the passes again (Film's recording draws many pictures
     *  between two of the loop's callbacks: without it every one after the first was the first again) */
    newFrame() { nodes(renderer)?.nodeFrame.update(); },
    /** Film's recording: on = the output shows the blend of a video frame's pictures (BlendNode); the output is rebuilt
     *  (its programs build at the next draw, synchronously: the recording's first step, which it does not keep). */
    blend(on) {
      if (B.on === !!on) return;
      B.on = !!on; B.last = true;
      pipeline.outputNode = outputNode(); pipeline.needsUpdate = true;
      if (B.on) { B.pipe = new THREE.RenderPipeline(renderer); B.pipe.outputColorTransform = false; B.pipe.outputNode = B.node.getTextureNode(); } // (draws the blend to the canvas too: overwritten by the frame's last picture)
      else { B.pipe?.dispose(); B.pipe = null; }
    },
    /** Film's recording: the next draw is picture `j` of its video frame (from 0); `last` = the frame's last one, whose
     *  draw runs the bloom and the output on the blend */
    blendPicture(j, last) { if (!B.node) return; B.node.weight = 1 / (j + 1); B.last = !!last; },
    // WS-72 step 6 (warm start), WS-75: the render pipelines of everything under `root`, built as the scene pass draws
    // them (its own target bound: MSAA, HalfFloat), all at once (compileAsync), so the first draw does not build them one
    // by one. The full-screen passes build on the first frame (a handful, small).
    // VIS-267: step(done, total) as each object's nodes are built and each pipeline compiles (objects + pipelines started
    // by now; only forward), so the loader's bar moves and the start's watchdog hears it (main.js). Batches of 4 on the
    // WebGL2 backend only (set up above).
    prewarm(root, step = null) {
      const prev = renderer.getRenderTarget();
      parallelPipelines(renderer); // WS-75 perf
      let p, objs = { loaded: 0, total: 0 }, best = 0, off = null;
      const c0 = compiles();
      const tell = () => {
        const c = compiles(), total = objs.total + c.started - c0.started, done = Math.min(total, objs.loaded + c.done - c0.done);
        if (total > 0 && done / total > best) { best = done / total; step(done, total); }
      };
      const onProgress = step && ((e) => { objs = e; tell(); });
      if (step) off = onCompiled(tell);
      // WS-77 2R (the phone set's kept AO saw the empty scene). VIS-267 step 4: the chain is drawn BEFORE the scene's
      // compile, so compileAs knows the depth the scene pass really draws at (trackDepths); after it, the phone set's
      // warm start built every program for depth 2 and its first picture built them all again for the real one.
      if (root === scene && !postWarm) { postWarm = true; trackDepths(renderer); warmPost(); aoNode?.invalidate(); }
      try {
        renderer.setRenderTarget(scenePass.renderTarget);
        p = compileAs(renderer, scenePass.renderTarget, () => renderer.compileAsync(root, camera, scene, onProgress)); // WS-75 perf: the draw's own context, side by side
      } finally { renderer.setRenderTarget(prev); }
      return p.then(pending).finally(() => off?.());
    },
    // VIS-267: a whole picture drawn dry: every pipeline a draw alone reaches (each caster's shadow pass, an object the
    // compile's camera left out) starts compiling side by side in its real context, off the main thread, and nothing is
    // drawn (three skips a draw whose pipeline is still compiling); resolves once they are compiled. main.js, before the
    // first picture (which built them one by one, sync). The kept AO saw an empty scene.
    dry() {
      parallelPipelines(renderer);
      const p = dryDraw(renderer, api.renderOnce);
      aoNode?.invalidate();
      return p;
    },
    // WS-15 QA probe (no effect on normal frames): render one frame, then scan the HalfFloat buffers for non-finite
    // (NaN/Inf) and negative values: the raw scene (the scene pass target) and the finish target (before bloom).
    // Async (GPU readback); y is from the top (WebGPU convention).
    async probe() {
      if (deviceLost(renderer)) return null; // VIS-267: a lost device reads nothing back
      api.renderOnce();
      const scan = async (rt) => {
        const { width: w, height: h } = rt;
        const raw = await renderer.readRenderTargetPixelsAsync(rt, 0, 0, w, h);
        const buf = raw instanceof Uint16Array ? raw : new Uint16Array(raw.buffer, raw.byteOffset, raw.byteLength >> 1);
        const stride = Math.round(buf.length / h); // rows may be padded (WebGPU: 256-byte aligned)
        let nan = 0, inf = 0, neg = 0, max = 0, first = null;
        const half = (v) => { const e = (v >> 10) & 31, m = v & 1023; return e === 0 ? m * 5.960464477539063e-8 : e === 31 ? 0 : (1 + m / 1024) * 2 ** (e - 15); };
        let hot = 0, hx = 0, hy = 0, over15 = 0, over4 = 0, over16 = 0;
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
          const i = y * stride + x * 4;
          let bad = false;
          for (let ch = 0; ch < 4; ch++) {
            const v = buf[i + ch], e = (v >> 10) & 31;
            if (e === 31) { if (v & 1023) nan++; else inf++; if (!first) first = { x, y, ch }; bad = true; continue; }
            if (v & 0x8000) { if (v & 0x7fff) neg++; bad = ch < 3 || bad; continue; }
            if (e > max) max = e;
          }
          if (bad) continue;
          // WS-15 (round 4e): hottest pixel (linear HDR luminance) and how many pixels exceed 1.5 / 4 / 16
          const l = 0.2126 * half(buf[i]) + 0.7152 * half(buf[i + 1]) + 0.0722 * half(buf[i + 2]);
          if (l > 1.5) { over15++; if (l > 4) { over4++; if (l > 16) over16++; } }
          if (l > hot) { hot = l; hx = x; hy = y; }
        }
        return { w, h, nan, inf, neg, maxExp: max - 15, first, hot: { lum: +hot.toFixed(2), x: hx, y: hy, over15, over4, over16 } };
      };
      return { tier: tierName, scene: await scan(scenePass.renderTarget), post: finishTex?.renderTarget ? await scan(finishTex.renderTarget) : null };
    },
    /** WS-75: the scene pass's target (MSAA resolved, HalfFloat): rest.js compares pictures from it */
    get sceneTarget() { return scenePass.renderTarget; },
    /** WS-78 lane C: the 3D chain's device pixels per CSS pixel (<= the canvas's renderer.getPixelRatio()); sizes in
     *  device pixels of things drawn in the scene (star size) use this one */
    get drawRatio() { return inner; },
    /** WS-78 lane C: a capture (the Renders tab) draws the 3D chain at the canvas's own density, no cap, no adaptive step;
     *  takes effect at the next setSize (call it before, and fullDensity(false) before the size is put back). It is also
     *  the capture's switch (capture.js): the map drops its live shortcuts while it is on */
    fullDensity(on) { AD.fullOn = capture.on = !!on; },
    /** VIS-267 round 2 C (lazy/sharp.js, the phone set): on = the map is still (resting pace or asleep): the 3D chain
     *  draws at the start or the level, whichever is higher, the canvas at the top; off = the level again */
    sharp(on) { if (AD.sharp === !!on) return; AD.sharp = !!on; if (!on) AD.woke = performance.now(); setInner(); },
    /** VIS-267 round 2 C: the opening test's rule. low: the one step under the floor (0 = none), lowRatio, lowUp (see
     *  densityVerdict); fit(d) = the measured work at density d: the level to start at (the highest under the slow
     *  line, never over the pixel limit's start: only a real step under it is taken); force (QA ?sharplow=1) = the level
     *  `low` on every moving picture, the rule off; told(why) hears each step */
    sharpRule({ low = 0, lowRatio = 1, lowUp = Infinity, fit = null, force = false, told = null, info = null }) {
      Object.assign(AD, { low: force ? 0 : low, lowRatio, lowUp, told, info });
      const now = performance.now();
      if (force) { AD.on = false; setLevel(low, now, 'force'); } else if (fit && AD.on) { const s = Math.min(capNow(), startAt()); let d = s; while (d > FLOOR + 1e-6 && fit(d) > SLOW) d -= STEP; if (s - Math.max(FLOOR, d) > 0.05) setLevel(Math.max(FLOOR, d), now, 'start'); }
    },
    /** WS-78 lane C: the adaptive density's state (QA, ?perf): { on, level, inner, changes: [[s, why, density]] };
     *  lowered: a step down holds (false at the pixel limit's start) */
    get adaptive() { return { on: AD.on, inner, lowered: AD.level < Infinity, noLower: AD.noLower, failed: [...AD.failed.keys()], changes: AD.log.slice(), work: AD.gpu ? 'gpu' : 'interval', slowMs: +SLOW.toFixed(1), fastMs: +FAST.toFixed(1), lateMs: LATE ? +LATE.toFixed(1) : null, capHz: AD.capHz || null, floor: FLOOR,
      ...(AD.info && { sharp: { ...AD.info, still: AD.sharp, low: AD.low, taken: AD.no075 } }) }; },
    /** frame pacing (lazy/pace.js): the drawn rate changed, so the frames measured so far no longer count (a density step
     *  waits a full window at the new rate: pacing to ~60 comes first, the density steps after it) */
    restartAdapt() { AD.s.length = 0; AD.iv.length = 0; AD.changed = performance.now(); AD.check = 0; AD.gen++; },
    /** VIS-267 W4, the owner's phone run (lazy/perfparts.js, ?perf=parts only): the chain's own parts. density(v): the
     *  3D chain at v device px per CSS px with the adaptive density off; density(0) gives both back. ao / bloom: the
     *  nodes (the run forces the AO every frame and skips the bloom passes by swapping their updateBefore). ext: the
     *  finishing extension (lazy/atmos.js: its uniforms). Nothing else calls it. */
    testParts() {
      return { ao: aoNode, bloom: bloomPass, ext,
        density(v) {
          if (v > 0 && !(AD.fixed > 0)) { AD.onBefore = AD.on; AD.on = false; }
          if (!(v > 0) && AD.fixed > 0) { AD.on = AD.onBefore; api.restartAdapt(); }
          AD.fixed = v > 0 ? v : 0; setInner();
        } };
    },
    /** the phone set (PHONE) or HIGH / RESCUE: which render set this page draws (QA, ?perf) */
    get set() { return T.label.toLowerCase(); },
    /** full-screen passes of the last picture (scene, AO and its blur, finish, bloom, output; shadow passes not counted)
     *  and the set's own numbers (PHONE.passes: before, moving, still). The phone set's AO kept: aoKept true. */
    get passes() {
      return { last: 1 + (aoNode ? aoNode.drawn : 0) + (finishTex ? 1 : 0) + (bloomPass ? bloomPass.passes.length : 0) + 1, aoKept: !!aoNode && aoNode.reuse > 0 && aoNode.drawn === 0, ...(T.passes && { set: T.passes }) };
    },
  };
  return api;
}

// the GPU's name (QA, perf readout): WebGPU adapter info, or the WebGL2 backend's debug renderer string. VIS-267: main.js
// hands the fallback's string to errors.js setDevice, which keeps only the chip's maker (glVendor), never the name.
export function gpuName(renderer) {
  try {
    const b = renderer.backend;
    if (b.isWebGPUBackend) { const i = b.device?.adapterInfo || {}; return String(i.description || [i.vendor, i.architecture].filter(Boolean).join(' ') || 'webgpu').toLowerCase(); }
    const gl = b.gl, dbg = gl?.getExtension('WEBGL_debug_renderer_info');
    return String(dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl?.getParameter(gl.RENDERER) || 'webgl2').toLowerCase();
  } catch { return 'unknown'; } // errors:ignore: the name is informational
}
